import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Footer from '../components/Footer';
import FloatingThemeToggle from '../components/FloatingThemeToggle';

const TermsPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <FloatingThemeToggle />
      <div className="bg-gradient-to-br from-gray-900 light:from-gray-50 via-gray-800 light:via-gray-100 to-twitch-900 light:to-twitch-100 flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-white light:text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-gray-400 light:text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="bg-gray-800/50 light:bg-white/50 rounded-lg p-8 space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Acceptance of Terms</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  By accessing and using Greetsy, you accept and agree to be bound by the terms and provision 
                  of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Description of Service</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  Greetsy is a web application that helps Twitch streamers track and manage greetings to 
                  their chat participants. The service connects to your Twitch account to monitor chat 
                  activity and provides tools to mark chatters as greeted.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">User Responsibilities</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>As a user of Greetsy, you agree to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use the service only for lawful purposes</li>
                  <li>Not interfere with or disrupt the service</li>
                  <li>Not attempt to gain unauthorized access to our systems</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Respect the Twitch Terms of Service and Community Guidelines</li>
                  <li>Not use the service to harass or harm others</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Account Management</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  Your Greetsy account is linked to your Twitch account. You are responsible for maintaining 
                  the confidentiality of your account credentials and for all activities that occur under 
                  your account.
                </p>
                <p>
                  You may delete your account at any time through the Settings page. Account deletion will 
                  permanently remove all associated data and cannot be undone.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Service Availability</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  We strive to provide reliable service, but cannot guarantee 100% uptime. The service may 
                  be temporarily unavailable due to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Scheduled maintenance</li>
                  <li>Technical difficulties</li>
                  <li>Third-party service outages (including Twitch)</li>
                  <li>Force majeure events</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Intellectual Property</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  The Greetsy service, including its original content, features, and functionality, is owned 
                  by Greetsy and is protected by international copyright, trademark, patent, trade secret, 
                  and other intellectual property laws.
                </p>
                <p>
                  You retain ownership of any content you create using our service, but grant us permission 
                  to use it as necessary to provide the service.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Limitation of Liability</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  Greetsy is provided "as is" without any warranty. We shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages, including without limitation, loss 
                  of profits, data, use, goodwill, or other intangible losses.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Termination</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  We may terminate or suspend your account immediately, without prior notice or liability, 
                  for any reason whatsoever, including without limitation if you breach the Terms.
                </p>
                <p>
                  Upon termination, your right to use the service will cease immediately, and any data 
                  associated with your account may be deleted.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Changes to Terms</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  We reserve the right to modify or replace these Terms at any time. If a revision is material, 
                  we will try to provide at least 30 days notice prior to any new terms taking effect.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Contact Information</h2>
              <div className="text-gray-300 light:text-gray-700">
                <p>
                  If you have any questions about these Terms, please contact us at:{' '}
                  <a href="mailto:hello@overcode.tv" className="text-twitch-400 hover:text-twitch-300">
                    hello@overcode.tv
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default TermsPage; 